/**
 * Chatbot configuration and contextual questions
 * Hierarchical topic structure mirroring sidebar navigation
 * 
 * IMPORTANT: Questions are factual, objective, and answerable from loaded data
 * - Uses explicit "2024" and "orçamento 2025"
 * - Never uses "período homólogo" or "orçamento" without year
 * - No opinion-based or speculative questions
 */

export const HISTORY_STORAGE_KEY = 'abc_chatbot_history';
export const MAX_HISTORY_ITEMS = 20;
export const ALLOWED_FILE_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
export const ALLOWED_IMAGE_TYPES = '.png,.jpg,.jpeg,.webp';
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// HIERARCHICAL TOPIC STRUCTURE
// Mirrors sidebar navigation exactly
// ============================================

// Helper: Custos Operacionais children (Level 4)
const custosOperacionaisChildren = [
  { id: 'cmvmc', name: { pt: 'CMVMC', en: 'COGS' } },
  { id: 'custos-pessoal', name: { pt: 'Custos com Pessoal', en: 'Personnel Costs' } },
  { id: 'subcontratos', name: { pt: 'Subcontratos', en: 'Subcontracts' } },
  { id: 'gerais-administrativos', name: { pt: 'Gerais e Administrativos', en: 'G&A' } },
  { id: 'custos-comercializacao', name: { pt: 'Custos de Comercialização', en: 'Commercialization Costs' } },
];

// Helper: Custos Operacionais children for 2025 (with prefixed IDs)
const custosOperacionaisChildren2025 = custosOperacionaisChildren.map(item => ({
  ...item,
  id: `2025-${item.id}`
}));

// Helper: Demonstração de Resultados children (Level 3) - for Revisão 2025 (with prefixed IDs)
const demonstracaoResultadosChildren2025 = [
  { id: '2025-receitas', name: { pt: 'Receitas', en: 'Revenue' } },
  { 
    id: '2025-custos-operacionais', 
    name: { pt: 'Custos Operacionais', en: 'Operating Costs' },
    children: custosOperacionaisChildren2025
  },
  { id: '2025-ebitda', name: { pt: 'EBITDA', en: 'EBITDA' } },
  { id: '2025-resultados-nao-operacionais', name: { pt: 'Resultados Não Operacionais', en: 'Non-Operating Results' } },
  { id: '2025-resultados-financeiros', name: { pt: 'Resultados Financeiros', en: 'Financial Results' } },
  { id: '2025-imposto-industrial', name: { pt: 'Imposto Industrial', en: 'Corporate Tax' } },
  { id: '2025-resultado-liquido', name: { pt: 'Resultado Líquido', en: 'Net Result' } },
];

// Helper: Demonstração de Resultados children for 2026 (with prefixed IDs)
const custosOperacionaisChildren2026 = custosOperacionaisChildren.map(item => ({
  ...item,
  id: `2026-${item.id}`
}));

const demonstracaoResultadosChildren2026 = [
  { id: '2026-receitas', name: { pt: 'Receitas', en: 'Revenue' } },
  { 
    id: '2026-custos-operacionais', 
    name: { pt: 'Custos Operacionais', en: 'Operating Costs' },
    children: custosOperacionaisChildren2026
  },
  { id: '2026-ebitda', name: { pt: 'EBITDA', en: 'EBITDA' } },
  { id: '2026-resultados-nao-operacionais', name: { pt: 'Resultados Não Operacionais', en: 'Non-Operating Results' } },
  { id: '2026-resultados-financeiros', name: { pt: 'Resultados Financeiros', en: 'Financial Results' } },
  { id: '2026-imposto-industrial', name: { pt: 'Imposto Industrial', en: 'Corporate Tax' } },
  { id: '2026-resultado-liquido', name: { pt: 'Resultado Líquido', en: 'Net Result' } },
  { id: '2026-custo-unitario-quilate', name: { pt: 'Custo Unitário por Quilate', en: 'Unit Cost per Carat' } },
];

// Finance area children (existing structure moved here)
const financeAreaChildren = [
  // Execução Orçamental 2026 (FIRST)
  {
    id: 'execucao-orcamental-2026',
    name: { pt: 'Execução Orçamental 2026', en: 'Budget Execution 2026' },
    children: [
      {
        id: '2026-demonstracao-resultados',
        name: { pt: 'Demonstração de Resultados', en: 'Income Statement' },
        children: demonstracaoResultadosChildren2026
      },
      {
        id: '2026-liquidez-geracao-caixa',
        name: { pt: 'Liquidez e Geração de Caixa', en: 'Liquidity & Cash Generation' }
      },
      {
        id: '2026-divida-financiamento',
        name: { pt: 'Dívida e Financiamento', en: 'Debt & Financing' }
      },
      {
        id: '2026-capex',
        name: { pt: 'CAPEX', en: 'CAPEX' }
      },
      {
        id: '2026-balanco-patrimonial',
        name: { pt: 'Balanço Patrimonial', en: 'Balance Sheet' }
      },
    ]
  },
  // Revisão Financeira 2025 (SECOND)
  {
    id: 'revisao-financeira-2025',
    name: { pt: 'Revisão Financeira 2025', en: 'Financial Review 2025' },
    children: [
      {
        id: '2025-demonstracao-resultados',
        name: { pt: 'Demonstração de Resultados', en: 'Income Statement' },
        children: demonstracaoResultadosChildren2025
      },
      {
        id: '2025-liquidez-geracao-caixa',
        name: { pt: 'Liquidez e Geração de Caixa', en: 'Liquidity & Cash Generation' }
      },
      {
        id: '2025-divida-financiamento',
        name: { pt: 'Dívida e Financiamento', en: 'Debt & Financing' }
      },
      {
        id: '2025-capex',
        name: { pt: 'CAPEX', en: 'CAPEX' }
      },
      {
        id: '2025-balanco-patrimonial',
        name: { pt: 'Balanço Patrimonial', en: 'Balance Sheet' }
      },
    ]
  },
];

// Operations area children - mirrors OperationsPage.jsx sidebar structure
const operationsAreaChildren = [
  // Execução Operacional 2026
  {
    id: 'execucao-operacional-2026',
    name: { pt: 'Execução Operacional 2026', en: 'Operational Execution 2026' },
    children: [
      { id: 'mina-acesso-2026', name: { pt: 'Mina e Acesso ao Minério', en: 'Mine & Ore Access' } },
      { id: 'planta-recuperacao-2026', name: { pt: 'Planta e Recuperação', en: 'Plant & Recovery' } },
      { id: 'stocks-minerio-2026', name: { pt: 'Stocks de Minério', en: 'Ore Stocks' } },
      { id: 'producao-receita-2026', name: { pt: 'Produção e Receita', en: 'Production & Revenue' } },
      { id: 'eficiencia-operacional-2026', name: { pt: 'Eficiência Operacional', en: 'Operational Efficiency' } },
      { id: 'risco-entrega-2026', name: { pt: 'Risco e Entrega do Plano', en: 'Risk & Plan Delivery' } },
    ]
  },
  // Revisão Operacional 2025
  {
    id: 'revisao-operacional-2025',
    name: { pt: 'Revisão Operacional 2025', en: 'Operational Review 2025' },
    children: [
      { id: 'mina-acesso-2025', name: { pt: 'Mina e Acesso ao Minério', en: 'Mine & Ore Access' } },
      { id: 'planta-recuperacao-2025', name: { pt: 'Planta e Recuperação', en: 'Plant & Recovery' } },
      { id: 'stocks-minerio-2025', name: { pt: 'Stocks de Minério', en: 'Ore Stocks' } },
      { id: 'producao-receita-2025', name: { pt: 'Produção e Receita', en: 'Production & Revenue' } },
      { id: 'eficiencia-operacional-2025', name: { pt: 'Eficiência Operacional', en: 'Operational Efficiency' } },
      { id: 'risco-operacional-2025', name: { pt: 'Risco Operacional', en: 'Operational Risk' } },
    ]
  },
];

// Main topic hierarchy - Level 1: Analysis Areas
export const topicHierarchy = [
  // =============================
  // LEVEL 1: Área de Análise - Operações
  // =============================
  {
    id: 'area-operacoes',
    name: { pt: 'Operações', en: 'Operations' },
    children: operationsAreaChildren
  },
  
  // =============================
  // LEVEL 1: Área de Análise - Finanças
  // =============================
  {
    id: 'area-financas',
    name: { pt: 'Finanças', en: 'Finance' },
    children: financeAreaChildren
  },
  
  // =============================
  // LEVEL 1: Área de Análise - Recursos Humanos (Indisponível)
  // =============================
  {
    id: 'area-rh',
    name: { pt: 'Recursos Humanos', en: 'Human Resources' },
    disabled: true,
    disabledMessage: { 
      pt: 'Indisponível nesta fase do projecto piloto', 
      en: 'Unavailable in this pilot phase' 
    }
  },
  
  // =============================
  // LEVEL 1: Área de Análise - ESG (Indisponível)
  // =============================
  {
    id: 'area-esg',
    name: { pt: 'ESG', en: 'ESG' },
    disabled: true,
    disabledMessage: { 
      pt: 'Indisponível nesta fase do projecto piloto', 
      en: 'Unavailable in this pilot phase' 
    }
  },
];

// ============================================
// CONTEXTUAL QUESTIONS - REVISÃO FINANCEIRA 2025
// Factual, objective, answerable from loaded data
// Uses explicit "2024" and "orçamento 2025"
// ============================================

export const contextualQuestions = {
  // ========================================
  // DEFAULT - Perguntas genéricas
  // ========================================
  default: {
    pt: [
      "Qual o desvio face ao orçamento?",
      "Qual a evolução vs período homólogo?",
      "Quais os principais drivers de variação?"
    ],
    en: [
      "What is the variance vs budget?",
      "What is the evolution vs prior period?",
      "What are the main variation drivers?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Execução Orçamental 2026
  // Função: Performance e Entrega do Plano
  // ========================================
  'execucao-orcamental-2026': {
    pt: [
      "O EBITDA está acima ou abaixo do orçamento?",
      "Qual a margem EBITDA actual vs target?",
      "A conversão em caixa (FCO/EBITDA) está a cumprir?"
    ],
    en: [
      "Is EBITDA above or below budget?",
      "What is current EBITDA margin vs target?",
      "Is cash conversion (OCF/EBITDA) on track?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Demonstração de Resultados
  // Função: Performance e Margem
  // ========================================
  'demonstracao-resultados': {
    pt: [
      "A receita está a bater o orçamento?",
      "Qual o desvio do EBITDA vs plano?",
      "O resultado líquido está em linha com expectativas?"
    ],
    en: [
      "Is revenue beating budget?",
      "What is EBITDA variance vs plan?",
      "Is net income in line with expectations?"
    ]
  },
  '2026-demonstracao-resultados': {
    pt: [
      "Receita 1T26: acima ou abaixo do orçamento?",
      "EBITDA 1T26: qual o desvio vs plano?",
      "Margem EBITDA: expandindo ou contraindo vs 1T25?"
    ],
    en: [
      "Q1 26 Revenue: above or below budget?",
      "Q1 26 EBITDA: what is variance vs plan?",
      "EBITDA Margin: expanding or contracting vs Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Receitas
  // Função: Performance de Vendas
  // ========================================
  'receitas': {
    pt: [
      "Qual foi a receita total no 1T26 e qual o desvio face ao orçamento?",
      "Quanto da receita total foi gerado por pedras regulares e quanto por pedras especiais?",
      "Qual foi o nível de concentração dos Top 5 clientes na receita?"
    ],
    en: [
      "What was total revenue in Q1 26 and variance vs budget?",
      "How much revenue came from regular vs special stones?",
      "What was the Top 5 customer concentration in revenue?"
    ]
  },
  '2026-receitas': {
    pt: [
      "Qual foi a receita total no 1T26 e qual o desvio face ao orçamento?",
      "Quanto da receita total foi gerado por pedras regulares e quanto por pedras especiais?",
      "Qual foi o nível de concentração dos Top 5 clientes na receita?"
    ],
    en: [
      "What was total revenue in Q1 26 and variance vs budget?",
      "How much revenue came from regular vs special stones?",
      "What was the Top 5 customer concentration in revenue?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Custos Operacionais
  // Função: Pressão e Eficiência
  // ========================================
  'custos-operacionais': {
    pt: [
      "Custos operacionais: acima ou abaixo do orçamento?",
      "Qual a rubrica com maior desvio?",
      "O rácio OPEX/Receita está a melhorar?"
    ],
    en: [
      "Operating costs: above or below budget?",
      "Which line item has the largest variance?",
      "Is OPEX/Revenue ratio improving?"
    ]
  },
  '2026-custos-operacionais': {
    pt: [
      "OPEX 1T26: dentro do envelope orçamental?",
      "Pessoal/Receita: rácio controlado?",
      "G&A/Receita: em linha com target?"
    ],
    en: [
      "Q1 26 OPEX: within budget envelope?",
      "Personnel/Revenue: ratio controlled?",
      "G&A/Revenue: in line with target?"
    ]
  },
  
  // ========================================
  // FINANÇAS - CMVMC
  // Função: Custo Directo de Produção
  // ========================================
  'cmvmc': {
    pt: [
      "Qual foi o CMVMC total no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o peso do CMVMC nos custos operacionais face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o CMVMC por m³ de minério movimentado face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total COGS in Q1 26 vs Budget Q1 26 and Q1 25?",
      "How did COGS weight in operating costs evolve vs Budget Q1 26 and Q1 25?",
      "How did COGS per m³ of ore moved evolve vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-cmvmc': {
    pt: [
      "Qual foi o CMVMC total no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o peso do CMVMC nos custos operacionais face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o CMVMC por m³ de minério movimentado face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total COGS in Q1 26 vs Budget Q1 26 and Q1 25?",
      "How did COGS weight in operating costs evolve vs Budget Q1 26 and Q1 25?",
      "How did COGS per m³ of ore moved evolve vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Custos com Pessoal
  // Função: Eficiência Laboral
  // ========================================
  'custos-pessoal': {
    pt: [
      "Qual foi o custo total com pessoal no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o peso dos custos com pessoal nos custos operacionais face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o custo médio por colaborador face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total personnel cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "How did personnel costs weight in operating costs evolve vs Budget Q1 26 and Q1 25?",
      "How did average cost per employee evolve vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-custos-pessoal': {
    pt: [
      "Qual foi o custo total com pessoal no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o peso dos custos com pessoal nos custos operacionais face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o custo médio por colaborador face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total personnel cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "How did personnel costs weight in operating costs evolve vs Budget Q1 26 and Q1 25?",
      "How did average cost per employee evolve vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Subcontratos
  // Função: Serviços Terceirizados
  // ========================================
  'subcontratos': {
    pt: [
      "Qual foi o custo total de Subcontratos no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo de Man Power face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo de Sondagem face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total Subcontracts cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was Man Power cost vs Budget Q1 26 and Q1 25?",
      "What was Drilling cost vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-subcontratos': {
    pt: [
      "Qual foi o custo total de Subcontratos no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo de Man Power face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo de Sondagem face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total Subcontracts cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was Man Power cost vs Budget Q1 26 and Q1 25?",
      "What was Drilling cost vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Gerais e Administrativos
  // Função: Custos de Estrutura
  // ========================================
  'gerais-administrativos': {
    pt: [
      "Qual foi o custo total de Gerais e Administrativos no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo de Serviços de Suporte face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo de Custos de Estrutura face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total G&A cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was Support Services cost vs Budget Q1 26 and Q1 25?",
      "What was Structure Costs vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-gerais-administrativos': {
    pt: [
      "Qual foi o custo total de Gerais e Administrativos no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo de Serviços de Suporte face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo de Custos de Estrutura face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total G&A cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was Support Services cost vs Budget Q1 26 and Q1 25?",
      "What was Structure Costs vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Custos de Comercialização
  // Função: Custos de Venda
  // ========================================
  'custos-comercializacao': {
    pt: [
      "Qual foi o custo total de comercialização no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o peso dos custos de comercialização nas vendas face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a receita líquida após encargos de comercialização face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total commercialization cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "How did commercialization costs weight in sales evolve vs Budget Q1 26 and Q1 25?",
      "What was net revenue after commercialization charges vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-custos-comercializacao': {
    pt: [
      "Qual foi o custo total de comercialização no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Como evoluiu o peso dos custos de comercialização nas vendas face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a receita líquida após encargos de comercialização face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total commercialization cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "How did commercialization costs weight in sales evolve vs Budget Q1 26 and Q1 25?",
      "What was net revenue after commercialization charges vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - EBITDA
  // Função: Rentabilidade Operacional
  // ========================================
  'ebitda': {
    pt: [
      "Qual foi o EBITDA no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a margem EBITDA no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o EBITDA por quilate vendido no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was EBITDA in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was EBITDA margin in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was EBITDA per carat sold in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-ebitda': {
    pt: [
      "Qual foi o EBITDA no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a margem EBITDA no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o EBITDA por quilate vendido no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was EBITDA in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was EBITDA margin in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was EBITDA per carat sold in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Resultados Não Operacionais
  // Função: Itens Extraordinários
  // ========================================
  'resultados-nao-operacionais': {
    pt: [
      "Qual foi o total de resultados não operacionais no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o valor dos itens extraordinários no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o valor dos itens recorrentes no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total non-operating results in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was extraordinary items value in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was recurring items value in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-resultados-nao-operacionais': {
    pt: [
      "Qual foi o total de resultados não operacionais no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o valor dos itens extraordinários no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o valor dos itens recorrentes no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was total non-operating results in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was extraordinary items value in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was recurring items value in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Resultados Financeiros
  // Função: Juros e Câmbio
  // ========================================
  'resultados-financeiros': {
    pt: [
      "Qual foi o resultado financeiro líquido no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo com juros de empréstimos bancários no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o resultado cambial líquido no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was net financial result in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was bank loan interest cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was net foreign exchange result in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-resultados-financeiros': {
    pt: [
      "Qual foi o resultado financeiro líquido no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o custo com juros de empréstimos bancários no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o resultado cambial líquido no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was net financial result in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was bank loan interest cost in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was net foreign exchange result in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Imposto Industrial
  // Função: Carga Fiscal
  // ========================================
  'imposto-industrial': {
    pt: [
      "Qual foi o resultado antes de impostos no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o imposto sobre rendimentos no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a taxa efectiva de imposto no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was result before taxes in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was income tax in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was effective tax rate in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-imposto-industrial': {
    pt: [
      "Qual foi o resultado antes de impostos no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o imposto sobre rendimentos no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a taxa efectiva de imposto no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was result before taxes in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was income tax in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was effective tax rate in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Liquidez e Caixa
  // Função: Conversão e Liquidez
  // ========================================
  'liquidez-geracao-caixa': {
    pt: [
      "Qual foi o caixa final no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o fluxo de caixa operacional no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o free cash flow no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was final cash in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was operating cash flow in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was free cash flow in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-liquidez-geracao-caixa': {
    pt: [
      "Qual foi o caixa final no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o fluxo de caixa operacional no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o free cash flow no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was final cash in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was operating cash flow in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was free cash flow in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Dívida e Financiamento
  // Função: Solidez e Alavancagem
  // ========================================
  'divida-financiamento': {
    pt: [
      "Qual foi a dívida bruta no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a dívida líquida no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o WACD no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was gross debt in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was net debt in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was WACD in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-divida-financiamento': {
    pt: [
      "Qual foi a dívida bruta no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a dívida líquida no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o WACD no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was gross debt in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was net debt in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was WACD in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - CAPEX
  // Função: Execução de Investimento
  // ========================================
  'capex': {
    pt: [
      "Qual foi o CAPEX executado no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a percentagem de CAPEX prioritário no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o montante reprogramado para 2026 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was executed CAPEX in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was priority CAPEX percentage in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was amount reprogrammed for 2026 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-capex': {
    pt: [
      "Qual foi o CAPEX executado no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a percentagem de CAPEX prioritário no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o montante reprogramado para 2026 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was executed CAPEX in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was priority CAPEX percentage in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was amount reprogrammed for 2026 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Balanço Patrimonial
  // Função: Solidez e Capital
  // ========================================
  'balanco-patrimonial': {
    pt: [
      "Qual foi a autonomia financeira no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o fundo de maneio no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a dívida líquida / EBITDA no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was financial autonomy in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was working capital in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was net debt / EBITDA in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-balanco-patrimonial': {
    pt: [
      "Qual foi a autonomia financeira no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o fundo de maneio no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a dívida líquida / EBITDA no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was financial autonomy in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was working capital in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was net debt / EBITDA in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Resultado Líquido
  // Função: Bottom Line
  // ========================================
  'resultado-liquido': {
    pt: [
      "Qual foi o resultado líquido no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a margem líquida no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o EBIT no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was net result in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was net margin in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was EBIT in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  '2026-resultado-liquido': {
    pt: [
      "Qual foi o resultado líquido no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi a margem líquida no 1T26 face ao Orçamento 1T26 e ao 1T25?",
      "Qual foi o EBIT no 1T26 face ao Orçamento 1T26 e ao 1T25?"
    ],
    en: [
      "What was net result in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was net margin in Q1 26 vs Budget Q1 26 and Q1 25?",
      "What was EBIT in Q1 26 vs Budget Q1 26 and Q1 25?"
    ]
  },
  
  // ========================================
  // OPERAÇÕES - Execução 2026
  // Função: Entrega do Plano Operacional
  // ========================================
  'execucao-operacional-2026': {
    pt: [
      "Produção (quilates): +/- quanto vs orçamento?",
      "Taxa de recuperação: qual a percentagem?",
      "Disponibilidade da planta: qual a percentagem?"
    ],
    en: [
      "Production (carats): +/- how much vs budget?",
      "Recovery rate: what percentage?",
      "Plant availability: what percentage?"
    ]
  },
  
  // ========================================
  // OPERAÇÕES - Mina e Acesso
  // Função: Upstream / Extracção
  // ========================================
  'mina-acesso-2026': {
    pt: [
      "Qual foi o volume de minério extraído no 1T26?",
      "Qual foi o volume de estéril removido face ao orçamento?",
      "Qual foi o strip ratio no 1T26?"
    ],
    en: [
      "What was the ore volume extracted in Q1 26?",
      "What was the waste volume removed vs budget?",
      "What was the strip ratio in Q1 26?"
    ]
  },
  'mina-acesso-2025': {
    pt: [
      "Qual foi o volume de minério extraído em 2025?",
      "Qual foi o volume de estéril removido face ao orçamento em 2025?",
      "Qual foi o nível de perfuração de produção registado no ano?"
    ],
    en: [
      "What was the ore volume extracted in 2025?",
      "What was the waste volume removed vs budget in 2025?",
      "What was the production drilling level recorded in the year?"
    ]
  },
  
  // ========================================
  // OPERAÇÕES - Planta e Recuperação
  // Função: Processamento e Rendimento
  // ========================================
  'planta-recuperacao-2026': {
    pt: [
      "Qual foi o volume de minério tratado no 1T26?",
      "Qual foi o teor de alimentação registado no trimestre?",
      "Quantas pedras especiais foram recuperadas no 1T26?"
    ],
    en: [
      "What was the ore volume treated in Q1 26?",
      "What was the feed grade recorded in the quarter?",
      "How many special stones were recovered in Q1 26?"
    ]
  },
  'planta-recuperacao-2025': {
    pt: [
      "Qual foi o volume de minério tratado em 2025?",
      "Qual foi o teor médio de alimentação registado no ano?",
      "Quantas pedras especiais foram recuperadas em 2025?"
    ],
    en: [
      "What was the ore volume treated in 2025?",
      "What was the average feed grade recorded in the year?",
      "How many special stones were recovered in 2025?"
    ]
  },
  
  // ========================================
  // OPERAÇÕES - Stocks
  // Função: Buffer e Continuidade
  // ========================================
  'stocks-minerio-2026': {
    pt: [
      "Qual foi o volume de stockpile no 1T26?",
      "Qual foi o volume de alimentação proveniente de stockpile?",
      "Qual foi o peso do stockpile no blend de alimentação?"
    ],
    en: [
      "What was the stockpile volume in Q1 26?",
      "What was the feed volume from stockpile?",
      "What was the stockpile weight in the feed blend?"
    ]
  },
  'stocks-minerio-2025': {
    pt: [
      "Qual foi o stockpile de fecho em 2025?",
      "Qual foi o volume de alimentação proveniente de stockpile no ano?",
      "Qual foi o peso do stockpile no blend de alimentação em 2025?"
    ],
    en: [
      "What was the closing stockpile in 2025?",
      "What was the feed volume from stockpile in the year?",
      "What was the stockpile weight in the feed blend in 2025?"
    ]
  },
  
  // ========================================
  // OPERAÇÕES - Produção e Receita
  // Função: Output e Valor
  // ========================================
  'producao-receita-2026': {
    pt: [
      "Quantos quilates foram recuperados no 1T26?",
      "Qual foi o teor registado no 1T26?",
      "Quantas pedras especiais foram recuperadas no 1T26?"
    ],
    en: [
      "How many carats were recovered in Q1 26?",
      "What was the grade recorded in Q1 26?",
      "How many special stones were recovered in Q1 26?"
    ]
  },
  'producao-receita-2025': {
    pt: [
      "Quantos quilates foram recuperados em 2025?",
      "Qual foi o tamanho médio da pedra registado no ano?",
      "Qual foi o número de pedras especiais face ao orçamento em 2025?"
    ],
    en: [
      "How many carats were recovered in 2025?",
      "What was the average stone size recorded in the year?",
      "What was the number of special stones vs budget in 2025?"
    ]
  },
  
  // ========================================
  // OPERAÇÕES - Eficiência
  // Função: Produtividade e Custo
  // ========================================
  'eficiencia-operacional-2026': {
    pt: [
      "Qual foi a disponibilidade da planta no 1T26?",
      "Qual foi a utilização efectiva da planta no trimestre?",
      "Qual foi a taxa de transferência DMS no 1T26?"
    ],
    en: [
      "What was the plant availability in Q1 26?",
      "What was the effective plant utilization in the quarter?",
      "What was the DMS transfer rate in Q1 26?"
    ]
  },
  'eficiencia-operacional-2025': {
    pt: [
      "Qual foi a disponibilidade da planta em 2025?",
      "Qual foi a utilização efectiva da planta no ano?",
      "Quantas horas de paragem da planta foram registadas em 2025?"
    ],
    en: [
      "What was the plant availability in 2025?",
      "What was the effective plant utilization in the year?",
      "How many plant downtime hours were recorded in 2025?"
    ]
  },
  
  // ========================================
  // OPERAÇÕES - Risco e Entrega
  // Função: Ameaças ao Plano
  // ========================================
  'risco-entrega-2026': {
    pt: [
      "Qual foi o gap de quilates face ao orçamento no 1T26?",
      "Qual foi o volume total DMS no trimestre?",
      "Qual foi o volume não produtivo registado no 1T26?"
    ],
    en: [
      "What was the carat gap vs budget in Q1 26?",
      "What was the total DMS volume in the quarter?",
      "What was the non-productive volume recorded in Q1 26?"
    ]
  },
  'risco-operacional-2025': {
    pt: [
      "Qual foi o gap de quilates face ao orçamento em 2025?",
      "Qual foi o volume total DMS registado no ano?",
      "Qual foi o volume não produtivo em 2025?"
    ],
    en: [
      "What was the carat gap vs budget in 2025?",
      "What was the total DMS volume recorded in the year?",
      "What was the non-productive volume in 2025?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão Financeira 2025 (Ano Completo)
  // Comparações: 2025 Real vs Orçamento 2025 vs 2024 Real
  // ========================================
  'revisao-financeira-2025': {
    pt: [
      "Qual foi a receita total em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o EBITDA em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi a margem líquida em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was total revenue in 2025 vs Budget 2025 and 2024?",
      "What was EBITDA in 2025 vs Budget 2025 and 2024?",
      "What was net margin in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Receitas
  // ========================================
  '2025-receitas': {
    pt: [
      "Qual foi a receita total em 2025 face ao Orçamento 2025 e a 2024?",
      "Quanto da receita total foi gerado por pedras regulares e quanto por pedras especiais em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o nível de concentração dos Top 5 clientes na receita em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was total revenue in 2025 vs Budget 2025 and 2024?",
      "How much revenue was generated by regular stones and special stones in 2025 vs Budget 2025 and 2024?",
      "What was the Top 5 customer concentration in revenue in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Custos com Pessoal
  // ========================================
  '2025-custos-pessoal': {
    pt: [
      "Qual foi o custo total com pessoal em 2025 face ao Orçamento 2025 e a 2024?",
      "Como evoluiu o peso dos custos com pessoal nos custos operacionais face ao Orçamento 2025 e a 2024?",
      "Como evoluiu o custo médio por colaborador face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was total personnel cost in 2025 vs Budget 2025 and 2024?",
      "How did personnel costs weight in operating costs evolve vs Budget 2025 and 2024?",
      "How did average cost per employee evolve vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: CMVMC
  // ========================================
  '2025-cmvmc': {
    pt: [
      "Qual foi o CMVMC total em 2025 face ao Orçamento 2025 e a 2024?",
      "Como evoluiu o peso do CMVMC nos custos operacionais face ao Orçamento 2025 e a 2024?",
      "Como evoluiu o CMVMC por m³ de minério movimentado face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was total COGS in 2025 vs Budget 2025 and 2024?",
      "How did COGS weight in operating costs evolve vs Budget 2025 and 2024?",
      "How did COGS per m³ of ore moved evolve vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Custos de Comercialização
  // ========================================
  '2025-custos-comercializacao': {
    pt: [
      "Qual foi o custo total de comercialização em 2025 face ao Orçamento 2025 e a 2024?",
      "Como evoluiu o peso dos custos de comercialização nas vendas face ao Orçamento 2025 e a 2024?",
      "Qual foi a receita líquida após encargos de comercialização em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was total commercialization cost in 2025 vs Budget 2025 and 2024?",
      "How did commercialization costs weight in sales evolve vs Budget 2025 and 2024?",
      "What was net revenue after commercialization charges in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Gerais e Administrativos
  // ========================================
  '2025-gerais-administrativos': {
    pt: [
      "Qual foi o custo total de Gerais e Administrativos em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o custo de Serviços de Suporte em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o custo de Custos de Estrutura em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was total G&A cost in 2025 vs Budget 2025 and 2024?",
      "What was Support Services cost in 2025 vs Budget 2025 and 2024?",
      "What was Structure Costs in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Subcontratos
  // ========================================
  '2025-subcontratos': {
    pt: [
      "Qual foi o custo total de Subcontratos em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o custo de Man Power em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o custo de Sondagem em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was total Subcontracts cost in 2025 vs Budget 2025 and 2024?",
      "What was Man Power cost in 2025 vs Budget 2025 and 2024?",
      "What was Drilling cost in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: EBITDA
  // ========================================
  '2025-ebitda': {
    pt: [
      "Qual foi o EBITDA em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi a margem EBITDA em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o EBITDA por quilate vendido em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was EBITDA in 2025 vs Budget 2025 and 2024?",
      "What was EBITDA margin in 2025 vs Budget 2025 and 2024?",
      "What was EBITDA per carat sold in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Resultados Não Operacionais
  // ========================================
  '2025-resultados-nao-operacionais': {
    pt: [
      "Qual foi o total de resultados não operacionais em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o valor dos itens extraordinários em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o valor dos itens recorrentes em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was total non-operating results in 2025 vs Budget 2025 and 2024?",
      "What was extraordinary items value in 2025 vs Budget 2025 and 2024?",
      "What was recurring items value in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Resultados Financeiros
  // ========================================
  '2025-resultados-financeiros': {
    pt: [
      "Qual foi o resultado financeiro líquido em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o custo com juros de empréstimos bancários em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o resultado cambial líquido em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was net financial result in 2025 vs Budget 2025 and 2024?",
      "What was bank loan interest cost in 2025 vs Budget 2025 and 2024?",
      "What was net foreign exchange result in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Imposto Industrial
  // ========================================
  '2025-imposto-industrial': {
    pt: [
      "Qual foi o resultado antes de impostos em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o imposto sobre rendimentos em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi a taxa efectiva de imposto em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was result before taxes in 2025 vs Budget 2025 and 2024?",
      "What was income tax in 2025 vs Budget 2025 and 2024?",
      "What was effective tax rate in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Resultado Líquido
  // ========================================
  '2025-resultado-liquido': {
    pt: [
      "Qual foi o resultado líquido em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi a margem líquida em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o EBIT em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was net result in 2025 vs Budget 2025 and 2024?",
      "What was net margin in 2025 vs Budget 2025 and 2024?",
      "What was EBIT in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Liquidez e Geração de Caixa
  // ========================================
  '2025-liquidez-geracao-caixa': {
    pt: [
      "Qual foi o caixa final em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o fluxo de caixa operacional em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o free cash flow em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was final cash in 2025 vs Budget 2025 and 2024?",
      "What was operating cash flow in 2025 vs Budget 2025 and 2024?",
      "What was free cash flow in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Dívida e Financiamento
  // ========================================
  '2025-divida-financiamento': {
    pt: [
      "Qual foi a dívida bruta em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi a dívida líquida em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o WACD em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was gross debt in 2025 vs Budget 2025 and 2024?",
      "What was net debt in 2025 vs Budget 2025 and 2024?",
      "What was WACD in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: CAPEX
  // ========================================
  '2025-capex': {
    pt: [
      "Qual foi o CAPEX executado em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi a percentagem de CAPEX prioritário em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o montante reprogramado em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was executed CAPEX in 2025 vs Budget 2025 and 2024?",
      "What was priority CAPEX percentage in 2025 vs Budget 2025 and 2024?",
      "What was amount reprogrammed in 2025 vs Budget 2025 and 2024?"
    ]
  },
  
  // ========================================
  // FINANÇAS - Revisão 2025: Balanço Patrimonial
  // ========================================
  '2025-balanco-patrimonial': {
    pt: [
      "Qual foi a autonomia financeira em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi o fundo de maneio em 2025 face ao Orçamento 2025 e a 2024?",
      "Qual foi a dívida líquida / EBITDA em 2025 face ao Orçamento 2025 e a 2024?"
    ],
    en: [
      "What was financial autonomy in 2025 vs Budget 2025 and 2024?",
      "What was working capital in 2025 vs Budget 2025 and 2024?",
      "What was net debt / EBITDA in 2025 vs Budget 2025 and 2024?"
    ]
  },
  'revisao-operacional-2025': {
    pt: [
      "Produção 2025: atingiu target anual?",
      "Taxa de recuperação 2025: máximo histórico?",
      "Custo/quilate 2025: melhor que 2024?"
    ],
    en: [
      "2025 production: reached annual target?",
      "2025 recovery rate: historic high?",
      "2025 cost/carat: better than 2024?"
    ]
  },
  
};
// ============================================
// FOLLOW-UP SUGGESTIONS (after response)
// Factual and action-oriented
// ============================================
// Context-aware follow-up suggestions based on topic
export const contextualFollowUpSuggestions = {
  // Relatórios e Documentação
  reports: {
    pt: [
      "Quando foi publicado o último relatório de auditoria?",
      "Que tipos de relatórios posso gerar na plataforma?",
      "Quais são as fontes de dados dos relatórios?",
      "Como exportar um relatório em PDF?",
      "Qual a periodicidade dos relatórios internos?",
      "Quem é o auditor externo?"
    ],
    en: [
      "When was the last audit report published?",
      "What types of reports can I generate on the platform?",
      "What are the data sources for reports?",
      "How to export a report in PDF?",
      "What is the periodicity of internal reports?",
      "Who is the external auditor?"
    ]
  },
  // Receitas
  revenue: {
    pt: [
      "Mostrar decomposição mensal das receitas",
      "Comparar receitas com 2024",
      "Comparar receitas com orçamento",
      "Qual a receita por tipo de diamante?",
      "Qual o preço médio por quilate?",
      "Mostrar evolução trimestral das receitas"
    ],
    en: [
      "Show monthly revenue breakdown",
      "Compare revenue with 2024",
      "Compare revenue with budget",
      "What is revenue by diamond type?",
      "What is the average price per carat?",
      "Show quarterly revenue evolution"
    ]
  },
  // EBITDA
  ebitda: {
    pt: [
      "Qual a margem EBITDA actual?",
      "Mostrar ponte EBITDA",
      "Comparar EBITDA com ano anterior",
      "Quais os principais drivers do EBITDA?",
      "Qual a evolução da margem EBITDA?",
      "Mostrar contribuição das receitas vs custos"
    ],
    en: [
      "What is the current EBITDA margin?",
      "Show EBITDA bridge",
      "Compare EBITDA with previous year",
      "What are the main EBITDA drivers?",
      "What is the EBITDA margin evolution?",
      "Show revenue vs cost contribution"
    ]
  },
  // Custos Operacionais
  opex: {
    pt: [
      "Mostrar decomposição dos custos operacionais",
      "Quais as rubricas com maior desvio?",
      "Comparar OPEX com orçamento",
      "Qual o peso de cada categoria de custo?",
      "Mostrar evolução dos custos com pessoal",
      "Identificar principais drivers de custo"
    ],
    en: [
      "Show operating costs breakdown",
      "Which items have the largest variance?",
      "Compare OPEX with budget",
      "What is the weight of each cost category?",
      "Show personnel costs evolution",
      "Identify main cost drivers"
    ]
  },
  // Liquidez e Caixa
  liquidity: {
    pt: [
      "Qual a posição de caixa actual?",
      "Mostrar evolução do Free Cash Flow",
      "Qual o ciclo de conversão de caixa?",
      "Comparar liquidez com período anterior",
      "Mostrar fontes e usos de caixa",
      "Qual a previsão de caixa?"
    ],
    en: [
      "What is the current cash position?",
      "Show Free Cash Flow evolution",
      "What is the cash conversion cycle?",
      "Compare liquidity with previous period",
      "Show sources and uses of cash",
      "What is the cash forecast?"
    ]
  },
  // Dívida e Financiamento
  debt: {
    pt: [
      "Qual a estrutura da dívida?",
      "Mostrar perfil de vencimentos",
      "Qual o custo médio da dívida?",
      "Comparar alavancagem com período anterior",
      "Quais os covenants bancários?",
      "Qual o rácio dívida líquida/EBITDA?"
    ],
    en: [
      "What is the debt structure?",
      "Show maturity profile",
      "What is the average cost of debt?",
      "Compare leverage with previous period",
      "What are the bank covenants?",
      "What is the net debt/EBITDA ratio?"
    ]
  },
  // CAPEX
  capex: {
    pt: [
      "Qual a execução de CAPEX no período?",
      "Mostrar CAPEX por departamento",
      "Quais os principais investimentos?",
      "Comparar CAPEX com orçamento",
      "Qual o pipeline de projectos?",
      "Mostrar evolução do CAPEX"
    ],
    en: [
      "What is CAPEX execution in the period?",
      "Show CAPEX by department",
      "What are the main investments?",
      "Compare CAPEX with budget",
      "What is the project pipeline?",
      "Show CAPEX evolution"
    ]
  },
  // Balanço
  balance: {
    pt: [
      "Mostrar evolução do activo total",
      "Qual a estrutura do capital próprio?",
      "Comparar balanço com ano anterior",
      "Quais as principais variações patrimoniais?",
      "Mostrar rácios de solvabilidade",
      "Qual o valor do fundo de maneio?"
    ],
    en: [
      "Show total assets evolution",
      "What is the equity structure?",
      "Compare balance sheet with previous year",
      "What are the main equity changes?",
      "Show solvency ratios",
      "What is the working capital?"
    ]
  },
  // ESG e Segurança
  esg: {
    pt: [
      "Qual o índice LTIFR actual?",
      "Mostrar evolução dos indicadores de segurança",
      "Quantos incidentes ambientais ocorreram?",
      "Qual o investimento na comunidade?",
      "Mostrar taxa de reabilitação de terras",
      "Quais as certificações ISO activas?"
    ],
    en: [
      "What is the current LTIFR?",
      "Show safety indicators evolution",
      "How many environmental incidents occurred?",
      "What is the community investment?",
      "Show land rehabilitation rate",
      "What ISO certifications are active?"
    ]
  },
  // Recursos Humanos
  hr: {
    pt: [
      "Qual o número actual de efectivos?",
      "Mostrar evolução da rotatividade",
      "Qual o custo médio por colaborador?",
      "Comparar headcount com orçamento",
      "Mostrar distribuição por função",
      "Qual a taxa de preenchimento de funções críticas?"
    ],
    en: [
      "What is the current headcount?",
      "Show turnover evolution",
      "What is the average cost per employee?",
      "Compare headcount with budget",
      "Show distribution by function",
      "What is the critical roles fill rate?"
    ]
  },
  // Impostos e Resultado Líquido
  tax: {
    pt: [
      "Qual a taxa efectiva de imposto?",
      "Mostrar ponte EBITDA para Resultado Líquido",
      "Qual o Resultado Líquido do período?",
      "Comparar margem líquida com ano anterior",
      "Qual a política de dividendos?",
      "Mostrar evolução do RAI"
    ],
    en: [
      "What is the effective tax rate?",
      "Show EBITDA to Net Result bridge",
      "What is the Net Result for the period?",
      "Compare net margin with previous year",
      "What is the dividend policy?",
      "Show EBT evolution"
    ]
  },
  // Default/General
  default: {
    pt: [
      "Qual o resumo executivo do período?",
      "Mostrar principais KPIs",
      "Quais os principais riscos identificados?",
      "Comparar performance com orçamento",
      "Mostrar alertas activos",
      "Qual a situação geral da empresa?"
    ],
    en: [
      "What is the executive summary for the period?",
      "Show main KPIs",
      "What are the main risks identified?",
      "Compare performance with budget",
      "Show active alerts",
      "What is the company's overall situation?"
    ]
  }
};

// Function to detect topic from message/response
export const detectTopicFromMessage = (message) => {
  const lowerMessage = message.toLowerCase();
  
  // Reports keywords
  if (lowerMessage.includes('relatório') || lowerMessage.includes('report') || 
      lowerMessage.includes('auditoria') || lowerMessage.includes('auditor') ||
      lowerMessage.includes('documentação') || lowerMessage.includes('certificação') ||
      lowerMessage.includes('publicado') || lowerMessage.includes('exportar')) {
    return 'reports';
  }
  
  // Revenue keywords
  if (lowerMessage.includes('receita') || lowerMessage.includes('revenue') ||
      lowerMessage.includes('vendas') || lowerMessage.includes('sales') ||
      lowerMessage.includes('preço por quilate') || lowerMessage.includes('price per carat')) {
    return 'revenue';
  }
  
  // EBITDA keywords
  if (lowerMessage.includes('ebitda') || lowerMessage.includes('margem operacional')) {
    return 'ebitda';
  }
  
  // Operating costs keywords
  if (lowerMessage.includes('custo') && (lowerMessage.includes('operacion') || lowerMessage.includes('opex')) ||
      lowerMessage.includes('cmvmc') || lowerMessage.includes('cogs') ||
      lowerMessage.includes('pessoal') || lowerMessage.includes('personnel') ||
      lowerMessage.includes('subcontrat') || lowerMessage.includes('comercialização')) {
    return 'opex';
  }
  
  // Liquidity keywords
  if (lowerMessage.includes('liquidez') || lowerMessage.includes('liquidity') ||
      lowerMessage.includes('caixa') || lowerMessage.includes('cash') ||
      lowerMessage.includes('free cash flow') || lowerMessage.includes('fcf')) {
    return 'liquidity';
  }
  
  // Debt keywords
  if (lowerMessage.includes('dívida') || lowerMessage.includes('debt') ||
      lowerMessage.includes('financiamento') || lowerMessage.includes('financing') ||
      lowerMessage.includes('banco') || lowerMessage.includes('bank') ||
      lowerMessage.includes('covenant') || lowerMessage.includes('alavancagem')) {
    return 'debt';
  }
  
  // CAPEX keywords
  if (lowerMessage.includes('capex') || lowerMessage.includes('investimento') ||
      lowerMessage.includes('investment') || lowerMessage.includes('projecto')) {
    return 'capex';
  }
  
  // Balance sheet keywords
  if (lowerMessage.includes('balanço') || lowerMessage.includes('balance') ||
      lowerMessage.includes('activo') || lowerMessage.includes('asset') ||
      lowerMessage.includes('passivo') || lowerMessage.includes('liabilit') ||
      lowerMessage.includes('capital próprio') || lowerMessage.includes('equity')) {
    return 'balance';
  }
  
  // ESG keywords
  if (lowerMessage.includes('esg') || lowerMessage.includes('segurança') || lowerMessage.includes('safety') ||
      lowerMessage.includes('ambiente') || lowerMessage.includes('environment') ||
      lowerMessage.includes('ltifr') || lowerMessage.includes('acidente') ||
      lowerMessage.includes('comunidade') || lowerMessage.includes('community') ||
      lowerMessage.includes('reabilitação') || lowerMessage.includes('rehabilitation')) {
    return 'esg';
  }
  
  // HR keywords
  if (lowerMessage.includes('recursos humanos') || lowerMessage.includes('human resources') ||
      lowerMessage.includes('efectivos') || lowerMessage.includes('headcount') ||
      lowerMessage.includes('rotatividade') || lowerMessage.includes('turnover') ||
      lowerMessage.includes('colaborador') || lowerMessage.includes('employee')) {
    return 'hr';
  }
  
  // Tax/Net result keywords
  if (lowerMessage.includes('imposto') || lowerMessage.includes('tax') ||
      lowerMessage.includes('resultado líquido') || lowerMessage.includes('net result') ||
      lowerMessage.includes('dividendo') || lowerMessage.includes('dividend') ||
      lowerMessage.includes('rai') || lowerMessage.includes('lucro')) {
    return 'tax';
  }
  
  return 'default';
};

// Get contextual suggestions based on detected topic
export const getContextualSuggestions = (message, language = 'pt') => {
  const topic = detectTopicFromMessage(message);
  const suggestions = contextualFollowUpSuggestions[topic] || contextualFollowUpSuggestions.default;
  return suggestions[language] || suggestions.pt;
};

// Legacy static suggestions (kept for backward compatibility)
export const followUpSuggestions = {
  pt: [
    "Mostrar decomposição mensal",
    "Comparar com 2024",
    "Comparar com orçamento 2025",
    "Identificar principais drivers",
    "Mostrar principais componentes",
    "Mostrar evolução trimestral",
    "Mostrar rubricas com maior desvio",
    "Mostrar contribuição por categoria",
    "Mostrar reconciliação entre 2025, 2024 e orçamento 2025"
  ],
  en: [
    "Show monthly breakdown",
    "Compare with 2024",
    "Compare with 2025 budget",
    "Identify main drivers",
    "Show main components",
    "Show quarterly evolution",
    "Show items with largest variance",
    "Show contribution by category",
    "Show reconciliation between 2025, 2024 and 2025 budget"
  ]
};

// Section names for display (flat mapping)
export const sectionNames = {
  default: { pt: 'Geral', en: 'General' },
  // Level 1: Areas
  'area-operacoes': { pt: 'Operações', en: 'Operations' },
  'area-financas': { pt: 'Finanças', en: 'Finance' },
  'area-rh': { pt: 'Recursos Humanos', en: 'Human Resources' },
  'area-esg': { pt: 'ESG', en: 'ESG' },
  // Operations - Main sections
  'execucao-operacional-2026': { pt: 'Execução Operacional 2026', en: 'Operational Execution 2026' },
  'revisao-operacional-2025': { pt: 'Revisão Operacional 2025', en: 'Operational Review 2025' },
  // Operations - 2026 subsections
  'mina-acesso-2026': { pt: 'Mina e Acesso ao Minério', en: 'Mine & Ore Access' },
  'planta-recuperacao-2026': { pt: 'Planta e Recuperação', en: 'Plant & Recovery' },
  'stocks-minerio-2026': { pt: 'Stocks de Minério', en: 'Ore Stocks' },
  'producao-receita-2026': { pt: 'Produção e Receita', en: 'Production & Revenue' },
  'eficiencia-operacional-2026': { pt: 'Eficiência Operacional', en: 'Operational Efficiency' },
  'risco-entrega-2026': { pt: 'Risco e Entrega do Plano', en: 'Risk & Plan Delivery' },
  // Operations - 2025 subsections
  'mina-acesso-2025': { pt: 'Mina e Acesso ao Minério', en: 'Mine & Ore Access' },
  'planta-recuperacao-2025': { pt: 'Planta e Recuperação', en: 'Plant & Recovery' },
  'stocks-minerio-2025': { pt: 'Stocks de Minério', en: 'Ore Stocks' },
  'producao-receita-2025': { pt: 'Produção e Receita', en: 'Production & Revenue' },
  'eficiencia-operacional-2025': { pt: 'Eficiência Operacional', en: 'Operational Efficiency' },
  'risco-operacional-2025': { pt: 'Risco Operacional', en: 'Operational Risk' },
  // Finance children
  'revisao-financeira-2025': { pt: 'Revisão Financeira 2025', en: 'Financial Review 2025' },
  'execucao-orcamental-2026': { pt: 'Execução Orçamental 2026', en: 'Budget Execution 2026' },
  'demonstracao-resultados': { pt: 'Demonstração de Resultados', en: 'Income Statement' },
  'receitas': { pt: 'Receitas', en: 'Revenue' },
  'custos-operacionais': { pt: 'Custos Operacionais', en: 'Operating Costs' },
  'cmvmc': { pt: 'CMVMC', en: 'COGS' },
  'custos-pessoal': { pt: 'Custos com Pessoal', en: 'Personnel Costs' },
  'subcontratos': { pt: 'Subcontratos', en: 'Subcontracts' },
  'gerais-administrativos': { pt: 'Gerais e Administrativos', en: 'G&A' },
  'custos-comercializacao': { pt: 'Custos de Comercialização', en: 'Commercialization Costs' },
  'ebitda': { pt: 'EBITDA', en: 'EBITDA' },
  'resultados-nao-operacionais': { pt: 'Resultados Não Operacionais', en: 'Non-Operating Results' },
  'resultados-financeiros': { pt: 'Resultados Financeiros', en: 'Financial Results' },
  'imposto-industrial': { pt: 'Imposto Industrial', en: 'Corporate Tax' },
  'resultado-liquido': { pt: 'Resultado Líquido', en: 'Net Result' },
  'custo-unitario-quilate': { pt: 'Custo Unitário por Quilate', en: 'Unit Cost per Carat' },
  'liquidez-geracao-caixa': { pt: 'Liquidez e Geração de Caixa', en: 'Liquidity & Cash Generation' },
  'divida-financiamento': { pt: 'Dívida e Financiamento', en: 'Debt & Financing' },
  'capex': { pt: 'CAPEX', en: 'CAPEX' },
  'balanco-patrimonial': { pt: 'Balanço Patrimonial', en: 'Balance Sheet' },
  // 2026 versions
  '2026-demonstracao-resultados': { pt: 'Demonstração de Resultados 2026', en: 'Income Statement 2026' },
  '2026-receitas': { pt: 'Receitas', en: 'Revenue' },
  '2026-custos-operacionais': { pt: 'Custos Operacionais', en: 'Operating Costs' },
  '2026-cmvmc': { pt: 'CMVMC', en: 'COGS' },
  '2026-custos-pessoal': { pt: 'Custos com Pessoal', en: 'Personnel Costs' },
  '2026-subcontratos': { pt: 'Subcontratos', en: 'Subcontracts' },
  '2026-gerais-administrativos': { pt: 'Gerais e Administrativos', en: 'G&A' },
  '2026-custos-comercializacao': { pt: 'Custos de Comercialização', en: 'Commercialization Costs' },
  '2026-ebitda': { pt: 'EBITDA', en: 'EBITDA' },
  '2026-resultados-nao-operacionais': { pt: 'Resultados Não Operacionais', en: 'Non-Operating Results' },
  '2026-resultados-financeiros': { pt: 'Resultados Financeiros', en: 'Financial Results' },
  '2026-imposto-industrial': { pt: 'Imposto Industrial', en: 'Corporate Tax' },
  '2026-resultado-liquido': { pt: 'Resultado Líquido', en: 'Net Result' },
  '2026-custo-unitario-quilate': { pt: 'Custo Unitário por Quilate', en: 'Unit Cost per Carat' },
  '2026-liquidez-geracao-caixa': { pt: 'Liquidez e Geração de Caixa', en: 'Liquidity & Cash Generation' },
  '2026-divida-financiamento': { pt: 'Dívida e Financiamento', en: 'Debt & Financing' },
  '2026-capex': { pt: 'CAPEX', en: 'CAPEX' },
  '2026-balanco-patrimonial': { pt: 'Balanço Patrimonial', en: 'Balance Sheet' },
  // 2025 versions (Revisão Financeira 2025)
  '2025-demonstracao-resultados': { pt: 'Demonstração de Resultados 2025', en: 'Income Statement 2025' },
  '2025-receitas': { pt: 'Receitas', en: 'Revenue' },
  '2025-custos-operacionais': { pt: 'Custos Operacionais', en: 'Operating Costs' },
  '2025-cmvmc': { pt: 'CMVMC', en: 'COGS' },
  '2025-custos-pessoal': { pt: 'Custos com Pessoal', en: 'Personnel Costs' },
  '2025-subcontratos': { pt: 'Subcontratos', en: 'Subcontracts' },
  '2025-gerais-administrativos': { pt: 'Gerais e Administrativos', en: 'G&A' },
  '2025-custos-comercializacao': { pt: 'Custos de Comercialização', en: 'Commercialization Costs' },
  '2025-ebitda': { pt: 'EBITDA', en: 'EBITDA' },
  '2025-resultados-nao-operacionais': { pt: 'Resultados Não Operacionais', en: 'Non-Operating Results' },
  '2025-resultados-financeiros': { pt: 'Resultados Financeiros', en: 'Financial Results' },
  '2025-imposto-industrial': { pt: 'Imposto Industrial', en: 'Corporate Tax' },
  '2025-resultado-liquido': { pt: 'Resultado Líquido', en: 'Net Result' },
  '2025-liquidez-geracao-caixa': { pt: 'Liquidez e Geração de Caixa', en: 'Liquidity & Cash Generation' },
  '2025-divida-financiamento': { pt: 'Dívida e Financiamento', en: 'Debt & Financing' },
  '2025-capex': { pt: 'CAPEX', en: 'CAPEX' },
  '2025-balanco-patrimonial': { pt: 'Balanço Patrimonial', en: 'Balance Sheet' },
};

// Legacy: topicSections for backwards compatibility
export const topicSections = topicHierarchy;

// URL section mapping
export const getSectionFromLocation = (location) => {
  const searchParams = new URLSearchParams(location.search);
  const section = searchParams.get('section');
  
  if (section) {
    return section;
  }
  
  const pathname = location.pathname;
  if (pathname.includes('/finance')) {
    if (pathname.includes('/revenue')) return 'receitas';
    if (pathname.includes('/ebitda')) return 'ebitda';
    if (pathname.includes('/cashflow')) return 'liquidez-geracao-caixa';
    if (pathname.includes('/debt')) return 'divida-financiamento';
    if (pathname.includes('/capex')) return 'capex';
    if (pathname.includes('/balance')) return 'balanco-patrimonial';
    return 'default';
  }
  
  return 'default';
};

// Helper function to get questions for a topic (handles prefixed IDs)
export const getQuestionsForTopic = (topicId) => {
  // First try to get the exact match for prefixed IDs
  if (contextualQuestions[topicId]) {
    return contextualQuestions[topicId];
  }
  // Remove 2025- or 2026- prefix if present to get base questions as fallback
  const baseId = topicId.replace('2025-', '').replace('2026-', '');
  return contextualQuestions[baseId] || contextualQuestions.default;
};

// Helper to check if a topic is from Revisão Financeira 2025 (has specific questions)
export const hasSpecificQuestions = (topicId) => {
  // First check exact match (for prefixed IDs like 2025-receitas)
  if (contextualQuestions.hasOwnProperty(topicId) && topicId !== 'default') {
    return true;
  }
  // Then check without prefix
  const baseId = topicId.replace('2025-', '').replace('2026-', '');
  return contextualQuestions.hasOwnProperty(baseId) && baseId !== 'default';
};
